import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Tabs,
  Tab,
  Autocomplete
} from '@mui/material';
import { Search, Cancel, Person, PlayArrow, Stop, Edit as EditIcon, EventNote, History as HistoryIcon, MyLocation, Close, Add, Delete as DeleteIcon } from '@mui/icons-material';
import { format, subDays, subMonths, startOfMonth, endOfMonth, startOfDay, endOfDay, startOfWeek, endOfWeek, addDays, parseISO } from 'date-fns';
import { parseTimeEntryDate, formatTimeEntry, toDatetimeLocalForEntry } from '../utils/dateUtils';
import { timeService } from '../services/timeService';
import { employeeService } from '../services/employeeService';
import { workFunctionService } from '../services/workFunctionService';
import { useAuth } from '../hooks/useAuth';
import { usePunchLocation } from '../hooks/usePunchLocation';

const WEEK_STARTS_SUNDAY = 0; // 0 = Sunday for date-fns
const OT_THRESHOLD_HOURS = 40;

const PRESETS = [
  { label: 'Today', getRange: () => ({ from: startOfDay(new Date()), to: endOfDay(new Date()) }) },
  { label: 'Last 7 Days', getRange: () => ({ from: startOfDay(subDays(new Date(), 6)), to: endOfDay(new Date()) }) },
  { label: 'Last 2 Weeks', getRange: () => ({ from: startOfDay(subDays(new Date(), 13)), to: endOfDay(new Date()) }) },
  { label: 'Last 30 Days', getRange: () => ({ from: startOfDay(subDays(new Date(), 29)), to: endOfDay(new Date()) }) },
  { label: 'This Month', getRange: () => ({ from: startOfMonth(new Date()), to: endOfDay(new Date()) }) },
  { label: 'Last Month', getRange: () => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return { from: startOfMonth(d), to: endOfMonth(d) };
  } }
];

const toYMD = (d) => (d ? format(d, 'yyyy-MM-dd') : '');
const toDatetimeLocal = (v) => toDatetimeLocalForEntry(v);

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WF_OTHER = '__other__';
const emptyPunch = () => ({ clockIn: '', clockOut: '', workOrderNumber: '', workFunctionId: '', workFunctionOther: '' });
function getEmptyWeekRows() {
  return DAY_NAMES.map((day, i) => ({
    day,
    dayIndex: i,
    punches: [emptyPunch()]
  }));
}
function addPunchToDay(rows, dayIndex) {
  const next = rows.map((r, i) =>
    i === dayIndex ? { ...r, punches: [...r.punches, emptyPunch()] } : r
  );
  return next;
}
function removePunchFromDay(rows, dayIndex, punchIndex) {
  const day = rows[dayIndex];
  if (!day || day.punches.length <= 1) return rows;
  const next = rows.map((r, i) =>
    i === dayIndex ? { ...r, punches: r.punches.filter((_, p) => p !== punchIndex) } : r
  );
  return next;
}

/** Group time entries by work week (Sun–Sat). Returns array of { weekStart, weekEnd, entries, straightHours, otHours }. */
function groupEntriesByWeek(entries) {
  const byWeek = new Map();
  for (const e of entries) {
    const t = e.clock_in ? parseTimeEntryDate(e.clock_in) : null;
    if (!t || isNaN(t.getTime())) continue;
    const weekStart = startOfWeek(t, { weekStartsOn: WEEK_STARTS_SUNDAY });
    const key = weekStart.getTime();
    if (!byWeek.has(key)) {
      byWeek.set(key, {
        weekStart,
        weekEnd: endOfWeek(t, { weekStartsOn: WEEK_STARTS_SUNDAY }),
        entries: []
      });
    }
    byWeek.get(key).entries.push(e);
  }
  const weeks = Array.from(byWeek.values()).sort((a, b) => a.weekStart - b.weekStart);
  weeks.forEach((w) => {
    const total = w.entries.reduce((s, e) => s + (parseFloat(e.hours_worked) || 0), 0);
    w.totalHours = total;
    w.straightHours = Math.min(OT_THRESHOLD_HOURS, total);
    w.otHours = Math.max(0, total - OT_THRESHOLD_HOURS);
  });
  return weeks;
}

const TimeTracking = () => {
  const { user } = useAuth();
  const currentUserId = user?.id ?? null;
  const isAdminOrManager = user?.role === 'admin' || user?.role === 'manager';
  const { getPunchPayload, locationError, setLocationError, requestLocationPermission, requestingPermission } = usePunchLocation();

  const [dateFrom, setDateFrom] = useState(toYMD(startOfDay(subMonths(new Date(), 1))));
  const [dateTo, setDateTo] = useState(toYMD(endOfDay(new Date())));
  const [employeeId, setEmployeeId] = useState('');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [workFunctions, setWorkFunctions] = useState([]);
  const [openPunch, setOpenPunch] = useState(null);
  const [punching, setPunching] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({
    clockIn: '',
    clockOut: '',
    hoursWorked: '',
    notes: '',
    workOrderId: '',
    workFunctionId: '',
    workFunctionOther: '',
    auditNotes: ''
  });
  const [editSaving, setEditSaving] = useState(false);
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [auditLogEntryId, setAuditLogEntryId] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [auditLogLoading, setAuditLogLoading] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0 = Search, 1 = Results
  const [employeeSearchInput, setEmployeeSearchInput] = useState('');
  const [manualWeekOpen, setManualWeekOpen] = useState(false);
  const [manualWeekDate, setManualWeekDate] = useState(() => toYMD(startOfWeek(new Date(), { weekStartsOn: WEEK_STARTS_SUNDAY })));
  const [manualWeekRows, setManualWeekRows] = useState(() => getEmptyWeekRows());
  const [manualWeekSaving, setManualWeekSaving] = useState(false);
  const [resultsEmployeeFilterId, setResultsEmployeeFilterId] = useState(null);
  const [weekViewOpen, setWeekViewOpen] = useState(false);
  const [weekViewLoading, setWeekViewLoading] = useState(false);
  const [weekViewData, setWeekViewData] = useState(null);

  const loadOpenPunch = async () => {
    if (!currentUserId) return;
    try {
      const res = await timeService.getAll({ employeeId: currentUserId, openOnly: 'true', limit: 1 });
      const list = res.data ?? [];
      setOpenPunch(list.length ? list[0] : null);
    } catch (e) {
      console.error('Error loading open punch:', e);
      setOpenPunch(null);
    }
  };

  useEffect(() => {
    loadOpenPunch();
  }, [currentUserId]);

  useEffect(() => {
    const load = async () => {
      try {
        setEmployeesLoading(true);
        const res = await employeeService.getAll({ activeOnly: 'true', limit: 500 });
        setEmployees(res.data ?? []);
      } catch (e) {
        console.error('Error loading employees:', e);
        setEmployees([]);
      } finally {
        setEmployeesLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await workFunctionService.getAll();
        setWorkFunctions(res.data ?? []);
      } catch (e) {
        console.error('Error loading work functions:', e);
        setWorkFunctions([]);
      }
    };
    load();
  }, []);

  const loadEntries = async () => {
    if (!dateFrom || !dateTo) {
      setError('Select date range (From and To).');
      return;
    }
    try {
      setLoading(true);
      setError('');
      const params = {
        dateFrom,
        dateTo,
        ...(employeeId ? { employeeId } : {})
      };
      const res = await timeService.getAll(params);
      const list = res.data ?? [];
      setEntries(list);
      setSelectedEntry(list.length > 0 ? list[0] : null);
      setActiveTab(1); // Show Results after search
    } catch (err) {
      console.error('Error loading time entries:', err);
      setError(err.response?.data?.message || 'Failed to load time entries');
    } finally {
      setLoading(false);
    }
  };

  const handleClockIn = async () => {
    if (!currentUserId) {
      setError('You must be logged in to clock in.');
      return;
    }
    try {
      setPunching(true);
      setError('');
      setLocationError(null);
      const payload = await getPunchPayload(false);
      await timeService.create({
        employeeId: currentUserId,
        ...payload
      });
      await loadOpenPunch();
      await loadEntries();
    } catch (err) {
      if (err.message && err.message.includes('Location')) {
        setError('Location is required to clock in. Please enable location and try again.');
      } else {
        setError(err.response?.data?.message || err.message || 'Failed to clock in');
      }
    } finally {
      setPunching(false);
    }
  };

  const handleClockOut = async () => {
    if (!openPunch?.id) {
      setError('No open punch to clock out.');
      return;
    }
    try {
      setPunching(true);
      setError('');
      setLocationError(null);
      const payload = await getPunchPayload(true);
      await timeService.update(openPunch.id, {
        clockOut: new Date().toISOString(),
        ...payload
      });
      setOpenPunch(null);
      await loadEntries();
    } catch (err) {
      if (err.message && err.message.includes('Location')) {
        setError('Location is required to clock out. Please enable location and try again.');
      } else {
        setError(err.response?.data?.message || err.message || 'Failed to clock out');
      }
    } finally {
      setPunching(false);
    }
  };

  const handleViewTimecard = async () => {
    if (!currentUserId) {
      setError('You must be logged in to view your timecard.');
      return;
    }
    const from = toYMD(startOfDay(subDays(new Date(), 6)));
    const to = toYMD(endOfDay(new Date()));
    setDateFrom(from);
    setDateTo(to);
    setEmployeeId(String(currentUserId));
    setActiveTab(1);
    try {
      setLoading(true);
      setError('');
      const res = await timeService.getAll({ dateFrom: from, dateTo: to, employeeId: currentUserId });
      setEntries(res.data ?? []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load timecard');
    } finally {
      setLoading(false);
    }
  };

  const openEditPunch = (row) => {
    setEditRow(row);
    setEditForm({
      clockIn: toDatetimeLocal(row.clock_in),
      clockOut: toDatetimeLocal(row.clock_out),
      hoursWorked: row.hours_worked != null ? String(row.hours_worked) : '',
      notes: row.notes ?? '',
      workOrderId: '',
      workOrderNumber: row.work_order_number ?? '',
      workFunctionId:
        row.work_function_other && row.work_function_id == null ? WF_OTHER : row.work_function_id != null ? String(row.work_function_id) : '',
      workFunctionOther: row.work_function_other ?? '',
      auditNotes: ''
    });
    setEditDialogOpen(true);
  };

  const handleSaveEditPunch = async () => {
    if (!editRow?.id) return;
    if (editForm.workFunctionId === WF_OTHER && !String(editForm.workFunctionOther || '').trim()) {
      setError('Enter a description when Function is Other.');
      return;
    }
    try {
      setEditSaving(true);
      setError('');
      await timeService.update(editRow.id, {
        clockIn: editForm.clockIn ? new Date(editForm.clockIn).toISOString() : undefined,
        clockOut: editForm.clockOut ? new Date(editForm.clockOut).toISOString() : undefined,
        hoursWorked: editForm.hoursWorked ? parseFloat(editForm.hoursWorked) : undefined,
        notes: editForm.notes || undefined,
        workOrderNumber: editForm.workOrderNumber || undefined,
        workFunctionId:
          editForm.workFunctionId === WF_OTHER ? '' : editForm.workFunctionId ? parseInt(editForm.workFunctionId, 10) : '',
        workFunctionOther:
          editForm.workFunctionId === WF_OTHER ? String(editForm.workFunctionOther || '').trim() : '',
        auditNotes: editForm.auditNotes || undefined
      });
      setEditDialogOpen(false);
      setEditRow(null);
      await loadEntries();
    } catch (err) {
      console.error('Error updating punch:', err);
      setError(err.response?.data?.message || 'Failed to update punch');
    } finally {
      setEditSaving(false);
    }
  };

  const openAuditLog = async (entryId) => {
    setAuditLogEntryId(entryId);
    setAuditLogOpen(true);
    setAuditLogLoading(true);
    setAuditLog([]);
    try {
      const res = await timeService.getAuditLog(entryId);
      setAuditLog(res.data ?? []);
    } catch (e) {
      console.error('Error loading audit log:', e);
      setAuditLog([]);
    } finally {
      setAuditLogLoading(false);
    }
  };

  const handlePreset = (preset) => {
    const { from, to } = preset.getRange();
    setDateFrom(toYMD(from));
    setDateTo(toYMD(to));
  };

  const selectedEmployee = useMemo(() => {
    if (!employeeId) return null;
    return employees.find((e) => String(e.id) === String(employeeId)) || null;
  }, [employeeId, employees]);

  const formatTime = (v) => formatTimeEntry(v, 'MM/dd/yyyy HH:mm');

  const fullName = (e) =>
    [e?.employee_first_name, e?.employee_last_name].filter(Boolean).join(' ') ||
    [e?.first_name, e?.last_name].filter(Boolean).join(' ') ||
    '—';

  const filteredEntries = useMemo(() => {
    if (!resultsEmployeeFilterId) return entries;
    return entries.filter((e) => String(e.employee_id) === String(resultsEmployeeFilterId));
  }, [entries, resultsEmployeeFilterId]);

  const weeks = useMemo(() => groupEntriesByWeek(filteredEntries), [filteredEntries]);

  const uniqueEmployeesInResults = useMemo(() => {
    const seen = new Set();
    const list = [];
    for (const e of entries) {
      const id = e.employee_id;
      if (id == null || seen.has(id)) continue;
      seen.add(id);
      list.push({
        id,
        employee_number: e.employee_number,
        name: fullName(e)
      });
    }
    return list.sort((a, b) => (a.employee_number || '').localeCompare(b.employee_number || '') || a.name.localeCompare(b.name));
  }, [entries]);

  const totalHours = useMemo(() => {
    return filteredEntries.reduce((sum, e) => sum + (parseFloat(e.hours_worked) || 0), 0);
  }, [filteredEntries]);

  const openWeekView = async (row) => {
    if (!row?.clock_in) return;
    const d = parseTimeEntryDate(row.clock_in) || new Date(row.clock_in);
    const weekStart = startOfWeek(d, { weekStartsOn: WEEK_STARTS_SUNDAY });
    const weekEnd = endOfWeek(d, { weekStartsOn: WEEK_STARTS_SUNDAY });
    setSelectedEntry(row);
    setWeekViewOpen(true);
    setWeekViewLoading(true);
    setWeekViewData(null);
    try {
      const res = await timeService.getAll({
        employeeId: row.employee_id,
        dateFrom: toYMD(weekStart),
        dateTo: toYMD(weekEnd),
        limit: 200
      });
      const list = res.data ?? [];
      const entriesByDay = [[], [], [], [], [], [], []];
      for (const entry of list) {
        if (!entry.clock_in) continue;
        const dayIndex = (parseTimeEntryDate(entry.clock_in) || new Date(entry.clock_in)).getDay();
        entriesByDay[dayIndex].push(entry);
      }
      entriesByDay.forEach((dayList) => dayList.sort((a, b) => (parseTimeEntryDate(a.clock_in) || new Date(a.clock_in)).getTime() - (parseTimeEntryDate(b.clock_in) || new Date(b.clock_in)).getTime()));
      setWeekViewData({
        weekStart,
        weekEnd,
        employeeName: fullName(row),
        employeeNumber: row.employee_number,
        entriesByDay
      });
    } catch (err) {
      console.error('Error loading week view:', err);
      setWeekViewData({ weekStart, weekEnd, employeeName: fullName(row), employeeNumber: row.employee_number, entriesByDay: [[], [], [], [], [], [], []], error: err.response?.data?.message || 'Failed to load week' });
    } finally {
      setWeekViewLoading(false);
    }
  };

  const openManualWeek = () => {
    const start = dateFrom ? parseISO(dateFrom) : new Date();
    const weekStart = startOfWeek(start, { weekStartsOn: WEEK_STARTS_SUNDAY });
    setManualWeekDate(toYMD(weekStart));
    setManualWeekRows(getEmptyWeekRows());
    setManualWeekOpen(true);
  };

  const handleSaveManualWeek = async () => {
    if (!employeeId || !selectedEmployee) {
      setError('Select an employee first (run a search with one employee).');
      return;
    }
    const weekStart = parseISO(manualWeekDate);
    try {
      setManualWeekSaving(true);
      setError('');
      for (let i = 0; i < 7; i++) {
        const dayRow = manualWeekRows[i];
        const d = addDays(weekStart, i);
        const dateStr = toYMD(d);
        for (const p of dayRow.punches || []) {
          if (!p.clockIn || !p.clockOut) continue;
          if (p.workFunctionId === WF_OTHER && !String(p.workFunctionOther || '').trim()) {
            setError('Enter a description for Function "Other" on each row that uses it.');
            setManualWeekSaving(false);
            return;
          }
          const clockInVal = `${dateStr}T${p.clockIn.length === 5 ? p.clockIn : '08:00'}`;
          const clockOutVal = `${dateStr}T${p.clockOut.length === 5 ? p.clockOut : '17:00'}`;
          await timeService.create({
            employeeId,
            workOrderNumber: p.workOrderNumber || undefined,
            workFunctionId: p.workFunctionId === WF_OTHER ? '' : p.workFunctionId || undefined,
            workFunctionOther: p.workFunctionId === WF_OTHER ? String(p.workFunctionOther || '').trim() : '',
            clockIn: new Date(clockInVal).toISOString(),
            clockOut: new Date(clockOutVal).toISOString(),
            latitude_in: 0,
            longitude_in: 0,
            device_name: 'Manual entry'
          });
        }
      }
      setManualWeekOpen(false);
      await loadEntries();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save manual time entries');
    } finally {
      setManualWeekSaving(false);
    }
  };

  const updateManualWeekPunch = (dayIndex, punchIndex, field, value) => {
    setManualWeekRows((prev) => {
      const next = prev.map((r, i) => {
        if (i !== dayIndex) return r;
        const punches = r.punches.map((p, pidx) =>
          pidx === punchIndex ? { ...p, [field]: value } : p
        );
        return { ...r, punches };
      });
      return next;
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Time Inquiry
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {locationError && (
        <Alert
          severity="warning"
          sx={{ mb: 1.5 }}
          onClose={() => setLocationError(null)}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<MyLocation />}
              onClick={requestLocationPermission}
              disabled={requestingPermission}
            >
              {requestingPermission ? 'Requesting…' : 'Allow Location'}
            </Button>
          }
        >
          {locationError}
        </Alert>
      )}

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tab label="Search" id="time-tab-search" aria-controls="time-tabpanel-search" />
        <Tab label="Results" id="time-tab-results" aria-controls="time-tabpanel-results" />
      </Tabs>

      {/* Tab 1: Search options only */}
      <div role="tabpanel" hidden={activeTab !== 0} id="time-tabpanel-search" aria-labelledby="time-tab-search">
        {activeTab === 0 && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
              Search options
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">Date presets:</Typography>
                {PRESETS.map((p) => (
                  <Chip
                    key={p.label}
                    label={p.label}
                    size="small"
                    onClick={() => handlePreset(p)}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
              <TextField
                size="small"
                label="Date From"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 160 }}
              />
              <TextField
                size="small"
                label="Date To"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 160 }}
              />
              <Autocomplete
                size="small"
                sx={{ minWidth: 260 }}
                options={employees}
                getOptionLabel={(emp) => (emp ? `${emp.employee_number || ''} ${[emp.first_name, emp.last_name].filter(Boolean).join(' ')}`.trim() : '')}
                inputValue={employeeSearchInput}
                onInputChange={(_, value) => setEmployeeSearchInput(value)}
                value={selectedEmployee ?? null}
                onChange={(_, newVal) => {
                  setEmployeeId(newVal ? String(newVal.id) : '');
                  if (!newVal) setEmployeeSearchInput('');
                }}
                isOptionEqualToValue={(opt, val) => opt && val && String(opt.id) === String(val.id)}
                renderInput={(params) => (
                  <TextField {...params} label="Employee" placeholder="Type name or number…" />
                )}
                filterOptions={(options, { inputValue }) => {
                  const q = (inputValue || '').trim().toLowerCase();
                  if (!q) return options;
                  return options.filter(
                    (o) =>
                      (o.employee_number && String(o.employee_number).toLowerCase().includes(q)) ||
                      (o.first_name && o.first_name.toLowerCase().includes(q)) ||
                      (o.last_name && o.last_name.toLowerCase().includes(q)) ||
                      `${(o.first_name || '')} ${(o.last_name || '')}`.toLowerCase().includes(q)
                  );
                }}
              />
              <Button variant="contained" size="small" startIcon={<Search />} onClick={loadEntries}>
                Search
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Cancel />}
                onClick={() => {
                  setDateFrom(toYMD(startOfDay(subMonths(new Date(), 1))));
                  setDateTo(toYMD(endOfDay(new Date())));
                  setEmployeeId('');
                  setEmployeeSearchInput('');
                  setEntries([]);
                  setError('');
                }}
              >
                Cancel
              </Button>
            </Box>
            {selectedEmployee && (
              <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person fontSize="small" color="action" />
                <Typography variant="body2">
                  Selected: {selectedEmployee.employee_number} — {fullName(selectedEmployee)}
                </Typography>
              </Box>
            )}
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Quick select — active employees
              </Typography>
              {employeesLoading ? (
                <CircularProgress size={20} />
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {employees.slice(0, 20).map((emp) => (
                    <Chip
                      key={emp.id}
                      size="small"
                      label={`${emp.employee_number} ${[emp.first_name, emp.last_name].filter(Boolean).join(' ')}`}
                      onClick={() => {
                        setEmployeeId(String(emp.id));
                        setEmployeeSearchInput(`${emp.employee_number} ${[emp.first_name, emp.last_name].filter(Boolean).join(' ')}`);
                      }}
                      color={String(employeeId) === String(emp.id) ? 'primary' : 'default'}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                  {employees.length > 20 && (
                    <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', ml: 0.5 }}>
                      +{employees.length - 20} more — type in search above
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Paper>
        )}
      </div>

      {/* Tab 2: Results — selected entry bar, clock actions, time ticket detail */}
      <div role="tabpanel" hidden={activeTab !== 1} id="time-tabpanel-results" aria-labelledby="time-tab-results">
        {activeTab === 1 && (
          <>
            {/* Selected Time Entry Information Bar */}
            <Paper sx={{ p: 1.5, mb: 2, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Employee</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {selectedEntry ? (selectedEntry.employee_number ? `${selectedEntry.employee_number} — ` : '') + fullName(selectedEntry) : '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Date</Typography>
                  <Typography variant="body2">{selectedEntry ? format(parseTimeEntryDate(selectedEntry.clock_in) || new Date(selectedEntry.clock_in), 'MM/dd/yyyy') : '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Work Order</Typography>
                  <Typography variant="body2">{selectedEntry?.work_order_identifier ?? selectedEntry?.work_order_number ?? '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Function</Typography>
                  <Typography variant="body2">{selectedEntry?.function_label ?? selectedEntry?.function_code ?? '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Clock In</Typography>
                  <Typography variant="body2">{selectedEntry ? formatTime(selectedEntry.clock_in) : '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Clock Out</Typography>
                  <Typography variant="body2">{selectedEntry ? formatTime(selectedEntry.clock_out) : '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Hours</Typography>
                  <Typography variant="body2">{selectedEntry?.hours_worked != null ? Number(selectedEntry.hours_worked).toFixed(2) : '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Notes</Typography>
                  <Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedEntry?.notes ?? '—'}</Typography>
                </Box>
                <Box sx={{ flexGrow: 1 }} />
                <IconButton size="small" onClick={() => setSelectedEntry(null)} disabled={!selectedEntry} sx={{ ml: 'auto' }} title="Clear selection">
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            </Paper>

            {/* Punch-style UI – Clock In/Out and View Timecard */}
            <Paper sx={{ p: 1.5, mb: 2, bgcolor: 'grey.50' }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<MyLocation />}
                  onClick={requestLocationPermission}
                  disabled={requestingPermission}
                  title="Click to trigger Chrome's location permission popup"
                >
                  {requestingPermission ? 'Requesting…' : 'Allow Location'}
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  size="small"
                  startIcon={<PlayArrow />}
                  onClick={handleClockIn}
                  disabled={punching || !currentUserId || !!openPunch}
                >
                  Clock In
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  startIcon={<Stop />}
                  onClick={handleClockOut}
                  disabled={punching || !openPunch}
                >
                  Clock Out
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EventNote />}
                  onClick={handleViewTimecard}
                  disabled={!currentUserId}
                >
                  View Timecard
                </Button>
                {isAdminOrManager && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Add />}
                    onClick={openManualWeek}
                    disabled={!employeeId}
                    title="Add manual time for selected employee (week template)"
                  >
                    Add week time
                  </Button>
                )}
                {openPunch && (
                  <Typography variant="body2" color="primary" sx={{ fontWeight: 600, ml: 0.5 }}>
                    Clocked in since {formatTime(openPunch.clock_in)}
                  </Typography>
                )}
              </Box>
            </Paper>

            {/* Quick select — employees in results */}
            {entries.length > 0 && (
              <Paper sx={{ p: 1.5, mb: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  Show timecards for
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                  <Chip
                    size="small"
                    label="All"
                    onClick={() => setResultsEmployeeFilterId(null)}
                    color={resultsEmployeeFilterId == null ? 'primary' : 'default'}
                    sx={{ cursor: 'pointer' }}
                  />
                  {uniqueEmployeesInResults.map((emp) => (
                    <Chip
                      key={emp.id}
                      size="small"
                      label={emp.employee_number ? `${emp.employee_number} ${emp.name}` : emp.name}
                      onClick={() => setResultsEmployeeFilterId(emp.id)}
                      color={String(resultsEmployeeFilterId) === String(emp.id) ? 'primary' : 'default'}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Paper>
            )}

            {/* Time ticket detail table — grouped by work week with OT */}
            <Paper sx={{ overflow: 'hidden' }}>
        <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Time ticket detail
          </Typography>
          {dateFrom && dateTo && (() => {
            const filterEmp = resultsEmployeeFilterId ? uniqueEmployeesInResults.find((e) => String(e.id) === String(resultsEmployeeFilterId)) : null;
            return (
              <Typography variant="caption" color="text.secondary">
                {dateFrom} — {dateTo}
                {filterEmp ? ` • Showing: ${filterEmp.employee_number || ''} ${filterEmp.name}`.trim() : selectedEmployee ? ` • ${fullName(selectedEmployee)}` : ''}
              </Typography>
            );
          })()}
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Date</Typography></TableCell>
                <TableCell><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Work Order</Typography></TableCell>
                <TableCell><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Function</Typography></TableCell>
                <TableCell><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Employee</Typography></TableCell>
                <TableCell><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Clock In</Typography></TableCell>
                <TableCell><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Clock Out</Typography></TableCell>
                <TableCell><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Hours</Typography></TableCell>
                <TableCell padding="none" width={40}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Edit</Typography></TableCell>
                {isAdminOrManager && <TableCell padding="none" width={40}><Typography variant="caption" sx={{ fontSize: '0.7rem' }}>History</Typography></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={isAdminOrManager ? 9 : 8} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdminOrManager ? 9 : 8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No time entries. Use the Search tab to set date range and employee (or leave empty for all), then Search.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredEntries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdminOrManager ? 9 : 8} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No time entries for the selected employee in this date range.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                weeks.map((w) => (
                  <React.Fragment key={w.weekStart.getTime()}>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell colSpan={isAdminOrManager ? 9 : 8} sx={{ py: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          Week of {format(w.weekStart, 'MMM d, yyyy')} (Sun) – {format(w.weekEnd, 'MMM d, yyyy')} (Sat)
                          {' · '}
                          Straight: {w.straightHours.toFixed(2)} hrs
                          {w.otHours > 0 && (
                            <> · OT: {w.otHours.toFixed(2)} hrs</>
                          )}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {w.entries.map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        sx={{
                          cursor: 'pointer',
                          backgroundColor: selectedEntry?.id === row.id ? 'action.selected' : 'inherit'
                        }}
                        onClick={() => openWeekView(row)}
                      >
                        <TableCell>{row.clock_in ? format(parseTimeEntryDate(row.clock_in) || new Date(row.clock_in), 'MM/dd/yyyy') : '—'}</TableCell>
                        <TableCell>{row.work_order_identifier || row.work_order_number || '—'}</TableCell>
                        <TableCell>{row.function_label || row.function_code || '—'}</TableCell>
                        <TableCell>
                          {row.employee_number ? `${row.employee_number} ` : ''}{fullName(row)}
                        </TableCell>
                        <TableCell>{formatTime(row.clock_in)}</TableCell>
                        <TableCell>{formatTime(row.clock_out)}</TableCell>
                        <TableCell>
                          {row.hours_worked != null ? Number(row.hours_worked).toFixed(2) : '—'}
                        </TableCell>
                        <TableCell padding="none">
                          <IconButton size="small" onClick={(ev) => { ev.stopPropagation(); openEditPunch(row); }} title="Edit punch" aria-label="Edit punch">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                        {isAdminOrManager && (
                          <TableCell padding="none">
                            <IconButton size="small" onClick={(ev) => { ev.stopPropagation(); openAuditLog(row.id); }} title="Audit history" aria-label="Audit history">
                              <HistoryIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {entries.length > 0 && (
          <Box sx={{ px: 1.5, py: 1, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Total hours: {totalHours.toFixed(2)}
            </Typography>
            {weeks.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                OT is calculated per week (over 40 hrs/week). Bi-weekly view shows each week’s straight vs OT separately.
              </Typography>
            )}
          </Box>
        )}
      </Paper>
          </>
        )}
      </div>

      {/* Manual week time entry dialog */}
      <Dialog open={manualWeekOpen} onClose={() => !manualWeekSaving && setManualWeekOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add week time (manual entry)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Work week is Sunday–Saturday. Add one or more clock in/out pairs per day (e.g. start, lunch out/in, end of day). Leave a pair blank to skip. OT is calculated per week (over 40 hrs).
          </Typography>
          {!employeeId && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Run a search with one employee selected to add time for that employee.
            </Alert>
          )}
          <TextField
            size="small"
            label="Week starting (Sunday)"
            type="date"
            value={manualWeekDate}
            onChange={(e) => setManualWeekDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2, width: 200 }}
          />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Day</TableCell>
                  <TableCell>Clock In</TableCell>
                  <TableCell>Clock Out</TableCell>
                  <TableCell>Work Order</TableCell>
                  <TableCell>Function</TableCell>
                  <TableCell padding="none" width={90}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {manualWeekRows.map((dayRow, dayIdx) =>
                  (dayRow.punches || [emptyPunch()]).map((punch, punchIdx) => (
                    <TableRow key={`${dayRow.day}-${punchIdx}`}>
                      <TableCell sx={{ fontWeight: 600, verticalAlign: 'middle' }}>
                        {punchIdx === 0 ? dayRow.day : ''}
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="time"
                          value={punch.clockIn}
                          onChange={(e) => updateManualWeekPunch(dayIdx, punchIdx, 'clockIn', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          inputProps={{ step: 300 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="time"
                          value={punch.clockOut}
                          onChange={(e) => updateManualWeekPunch(dayIdx, punchIdx, 'clockOut', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          inputProps={{ step: 300 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={punch.workOrderNumber}
                          onChange={(e) => updateManualWeekPunch(dayIdx, punchIdx, 'workOrderNumber', e.target.value)}
                          placeholder="e.g. 500"
                          fullWidth
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <FormControl fullWidth size="small">
                            <Select
                              value={punch.workFunctionId || ''}
                              displayEmpty
                              onChange={(e) => updateManualWeekPunch(dayIdx, punchIdx, 'workFunctionId', e.target.value)}
                            >
                              <MenuItem value="">—</MenuItem>
                              {workFunctions.map((wf) => (
                                <MenuItem key={wf.id} value={String(wf.id)}>{wf.label}</MenuItem>
                              ))}
                              <MenuItem value={WF_OTHER}>Other…</MenuItem>
                            </Select>
                          </FormControl>
                          {punch.workFunctionId === WF_OTHER && (
                            <TextField
                              size="small"
                              placeholder="Describe function"
                              value={punch.workFunctionOther || ''}
                              onChange={(e) =>
                                updateManualWeekPunch(dayIdx, punchIdx, 'workFunctionOther', e.target.value)
                              }
                              fullWidth
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell padding="none" sx={{ verticalAlign: 'middle' }}>
                        <IconButton
                          size="small"
                          onClick={() => setManualWeekRows((prev) => addPunchToDay(prev, dayIdx))}
                          title="Add another punch (e.g. lunch)"
                          aria-label="Add punch"
                        >
                          <Add fontSize="small" />
                        </IconButton>
                        {(dayRow.punches?.length ?? 1) > 1 && (
                          <IconButton
                            size="small"
                            onClick={() => setManualWeekRows((prev) => removePunchFromDay(prev, dayIdx, punchIdx))}
                            title="Remove this punch row"
                            aria-label="Remove punch"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualWeekOpen(false)} disabled={manualWeekSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveManualWeek} disabled={manualWeekSaving || !employeeId}>
            {manualWeekSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Week view dialog — existing punches for the week */}
      <Dialog open={weekViewOpen} onClose={() => setWeekViewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {weekViewData
            ? `Week of ${format(weekViewData.weekStart, 'MMM d, yyyy')} (Sun) – ${format(weekViewData.weekEnd, 'MMM d, yyyy')} (Sat)${weekViewData.employeeName ? ` — ${weekViewData.employeeNumber ? `${weekViewData.employeeNumber} ` : ''}${weekViewData.employeeName}` : ''}`
            : 'Week view'}
        </DialogTitle>
        <DialogContent>
          {weekViewLoading ? (
            <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={32} />
            </Box>
          ) : weekViewData?.error ? (
            <Alert severity="error">{weekViewData.error}</Alert>
          ) : weekViewData ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Day</TableCell>
                    <TableCell>Clock In</TableCell>
                    <TableCell>Clock Out</TableCell>
                    <TableCell align="right">Hours</TableCell>
                    <TableCell>Work Order</TableCell>
                    <TableCell>Function</TableCell>
                    {isAdminOrManager && <TableCell padding="none" width={40}>Edit</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {DAY_NAMES.map((dayName, dayIndex) => {
                    const dayEntries = weekViewData.entriesByDay[dayIndex] || [];
                    if (dayEntries.length === 0) {
                      return (
                        <TableRow key={dayName}>
                          <TableCell sx={{ fontWeight: 600 }}>{dayName}</TableCell>
                          <TableCell colSpan={isAdminOrManager ? 6 : 5}>
                            <Typography variant="body2" color="text.secondary">No punches</Typography>
                          </TableCell>
                        </TableRow>
                      );
                    }
                    return dayEntries.map((entry, punchIdx) => (
                      <TableRow key={entry.id || `${dayName}-${punchIdx}`}>
                        <TableCell sx={{ fontWeight: 600, verticalAlign: 'middle' }}>
                          {punchIdx === 0 ? dayName : ''}
                        </TableCell>
                        <TableCell>{formatTime(entry.clock_in)}</TableCell>
                        <TableCell>{formatTime(entry.clock_out)}</TableCell>
                        <TableCell align="right">
                          {entry.hours_worked != null ? Number(entry.hours_worked).toFixed(2) : '—'}
                        </TableCell>
                        <TableCell>{entry.work_order_identifier || entry.work_order_number || '—'}</TableCell>
                        <TableCell>{entry.function_label || entry.function_code || '—'}</TableCell>
                        {isAdminOrManager && (
                          <TableCell padding="none" sx={{ verticalAlign: 'middle' }}>
                            <IconButton
                              size="small"
                              onClick={() => { setWeekViewOpen(false); openEditPunch(entry); }}
                              title="Edit punch"
                              aria-label="Edit punch"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ));
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWeekViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Punch dialog */}
      <Dialog open={editDialogOpen} onClose={() => !editSaving && (setEditDialogOpen(false), setEditRow(null))} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Punch</DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5} sx={{ pt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Clock In"
                type="datetime-local"
                value={editForm.clockIn}
                onChange={(e) => setEditForm((f) => ({ ...f, clockIn: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Clock Out"
                type="datetime-local"
                value={editForm.clockOut}
                onChange={(e) => setEditForm((f) => ({ ...f, clockOut: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Job # (Work Order)"
                value={editForm.workOrderNumber}
                onChange={(e) => setEditForm((f) => ({ ...f, workOrderNumber: e.target.value }))}
                placeholder="e.g. 500"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Function</InputLabel>
                <Select
                  value={editForm.workFunctionId}
                  label="Function"
                  onChange={(e) =>
                    setEditForm((f) => ({
                      ...f,
                      workFunctionId: e.target.value,
                      workFunctionOther: e.target.value === WF_OTHER ? f.workFunctionOther : ''
                    }))
                  }
                >
                  <MenuItem value="">—</MenuItem>
                  {workFunctions.map((wf) => (
                    <MenuItem key={wf.id} value={String(wf.id)}>{wf.label}</MenuItem>
                  ))}
                  <MenuItem value={WF_OTHER}>Other…</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {editForm.workFunctionId === WF_OTHER && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Function (other)"
                  value={editForm.workFunctionOther}
                  onChange={(e) => setEditForm((f) => ({ ...f, workFunctionOther: e.target.value }))}
                  placeholder="e.g. one-off task"
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Hours"
                type="number"
                inputProps={{ step: 0.25, min: 0 }}
                value={editForm.hoursWorked}
                onChange={(e) => setEditForm((f) => ({ ...f, hoursWorked: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Notes"
                multiline
                rows={2}
                value={editForm.notes}
                onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </Grid>
            {isAdminOrManager && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Audit note (reason for edit)"
                  value={editForm.auditNotes}
                  onChange={(e) => setEditForm((f) => ({ ...f, auditNotes: e.target.value }))}
                  placeholder="Optional"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditDialogOpen(false); setEditRow(null); }} disabled={editSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEditPunch} disabled={editSaving}>
            {editSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Audit log dialog */}
      <Dialog open={auditLogOpen} onClose={() => setAuditLogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit history</DialogTitle>
        <DialogContent>
          {auditLogLoading ? (
            <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : auditLog.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No edit history for this time entry.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>Who</TableCell>
                    <TableCell>Field</TableCell>
                    <TableCell>Old</TableCell>
                    <TableCell>New</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditLog.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.changed_at ? format(new Date(row.changed_at), 'MM/dd HH:mm') : '—'}</TableCell>
                      <TableCell>{[row.changed_by_first_name, row.changed_by_last_name].filter(Boolean).join(' ') || '—'}</TableCell>
                      <TableCell>{row.field_name || '—'}</TableCell>
                      <TableCell sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.old_value ?? '—'}</TableCell>
                      <TableCell sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.new_value ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAuditLogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TimeTracking;
